import React, { useEffect, useState } from "react";
import axios from "axios";
import { Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { API } from "../../auth/AuthContext";
import { formatLocalTimestamp } from "../../lib/dateLocal";
import { toast } from "sonner";

export default function UserActivityModal({ user, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.user_id) {
      setEvents([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/users/${user.user_id}/activity`, { withCredentials: true });
        if (!cancelled) setEvents(r.data?.events || []);
      } catch (e) {
        if (!cancelled) toast.error("Could not load user activity");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id]);

  if (!user) return null;

  const typeLabel = (t) => ({
    kyc: "KYC",
    booking_rental: "Rental",
    booking_host: "Host booking",
    listing_created: "Listing",
    listing_status: "Listing status",
    listing_rejected: "Listing rejected",
    listing_note: "Listing note",
    wallet_txn: "Wallet",
    parking_listing_created: "Parking listing",
    parking_listing_status: "Parking status",
    parking_listing_rejected: "Parking rejected",
  }[t] || t.replace(/_/g, " "));

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-white border border-[var(--zc-border)] max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="user-activity-modal">
        <DialogHeader>
          <DialogTitle className="zc-headline text-2xl flex items-center gap-2">
            <Eye size={18} /> Activity — {user.name || user.email}
          </DialogTitle>
          <p className="text-xs text-[var(--zc-text-3)] mt-1">Includes listing rejection notes recorded by admins.</p>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 mt-2 -mx-1 px-1">
          {loading && <div className="py-8 text-center text-[var(--zc-text-3)] text-sm">Loading…</div>}
          {!loading && events.length === 0 && (
            <div className="py-8 text-center text-[var(--zc-text-3)] text-sm">No activity recorded yet.</div>
          )}
          {!loading && events.map((ev, i) => (
            <div key={`${ev.type}-${ev.ref_id}-${ev.at}-${i}`} className="py-3 border-b border-[var(--zc-border)] last:border-0 text-sm" data-testid={`activity-event-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="zc-badge text-[10px] uppercase tracking-wider mr-2">{typeLabel(ev.type)}</span>
                  <span className="font-semibold">{ev.summary}</span>
                  {ev.detail && <div className="text-xs text-[var(--zc-text-2)] mt-1">{ev.detail}</div>}
                  {ev.ref_id && <div className="text-[10px] font-mono text-[var(--zc-text-3)] mt-1">{ev.ref_id}</div>}
                </div>
                <div className="text-[10px] text-[var(--zc-text-3)] shrink-0">{ev.at ? formatLocalTimestamp(ev.at) : "—"}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-3 border-t border-[var(--zc-border)] mt-2">
          <button onClick={onClose} className="px-4 h-10 zc-btn-ghost text-sm">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
